import React, { useEffect, useMemo } from 'react';

import { MenuDivider, MenuItem } from '@chakra-ui/react';
import { Routes, Route, useParams, useNavigate, useLocation, useMatch } from 'react-router-dom';
import WideHeader from '../../component/Headers/WideHeader/WideHeader';
import Absences from '../../component/TenantAdminComponents/Absences/Absences';
import AdminSettings from '../../component/TenantAdminComponents/AdminSettings/AdminSettings';
import Kitchen from '../../component/TenantAdminComponents/Kitchen/Kitchen';
import MailBox from '../../component/TenantAdminComponents/MailBox/MailBox';
import SingleAdminMessage from '../../component/TenantAdminComponents/MailBox/Messages/SingleAdminMessage/SingleAdminMessage';
import Reports from '../../component/TenantAdminComponents/Reports/Reports';
import School from '../../component/TenantAdminComponents/School/School';
import Users from '../../component/TenantAdminComponents/Users/Users';

import { SideMenuOptionInterface } from '../../shared';
import IconComponent from '../../component/IconComponent/IconComponent';
import useAuth from '../../hooks/useAuth';
import { useSideNavContext } from '../../context/SideNavProvider';
import { Dashboard } from '../../component/TenantAdminComponents/Dashboard/Dashboard';
import Tutorials from '../../component/ChildOwnerComponents/Tutorials/Tutorials';
import { Instruction } from '../../shared/type/instruction.interface';
import { SingleAnnouncement } from '../../component/TenantAdminComponents/MailBox/SingleAnnouncement/SingleAnnouncement';
import { filterMenuByPermissions } from './util';
import { ProtectedRoute } from '../../component/ProtectedRoutes';
import { ParentDashboardContextProvider } from '../../context/ParentDashboardContext';
import { useAdminDashboardContext } from '../../context/AdminDashboardDataContext';
import { useUnreadThreadCountContext } from '../../context/UnreadMessageContext';
import Finances from '../../component/TenantAdminComponents/Finances/Finances';

const TenantAdminDashboard = () => {
  const params = useParams();
  const match = useMatch('/tenantAdmin/id/:tenantId/*');
  const tenant = match?.params.tenantId || params.id;

  const { auth, logout } = useAuth();
  const { setNavOptions, toggleShowNav, setToggleMode } = useSideNavContext();
  const { setUnreadThreadCount, unreadThreadCount } = useUnreadThreadCountContext();

  const { dashboardData } = useAdminDashboardContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setToggleMode('minimal');
  }, []);

  const menuOptions: SideMenuOptionInterface[] = useMemo(
    () => [
      {
        header: 'Strona Główna',
        icon: 'happyHomeIcon',
        link: `id/${tenant}/dashboard`,
        permission: 'ORDERS'
      },
      {
        header: 'Zamówienia',
        icon: 'calendar',
        link: `id/${tenant}/absences`,
        permission: 'ORDERS'
      },
      {
        header: 'Kuchnia',
        icon: 'cutlery',
        permission: 'MEAL_COOKING',
        children: [
          {
            header: 'Jadłospis',
            icon: 'bookOpen2',
            link: `id/${tenant}/kitchen/food-menu/meal-plan`,
            permission: 'MEAL_COOKING'
          },
          {
            header: 'Alergeny',
            icon: 'alertTriangle',
            link: `id/${tenant}/kitchen/allergens`,
            permission: 'MEAL_COOKING'
          },
          {
            header: 'Podsumowanie',
            icon: 'layoutAlt03',
            link: `id/${tenant}/kitchen/summary`,
            permission: 'MEAL_COOKING'
          }
        ]
      },
      {
        header: 'Użytkownicy',
        icon: 'users',
        permission: 'TENANT_USERS',
        children: [
          {
            header: 'Uczniowie',
            icon: 'faceSmile',
            link: `id/${tenant}/users/children/children-table`,
            permission: 'TENANT_USERS'
          },
          {
            header: 'Rodzice',
            icon: 'users2',
            link: `id/${tenant}/users/parents/parents-table`,
            permission: 'TENANT_USERS'
          },
          {
            header: 'Nauczyciele',
            icon: 'graduationHat',
            link: `id/${tenant}/users/teachers/teachers-table`,
            permission: 'TENANT_USERS'
          },
          {
            header: 'Pracownicy',
            icon: 'userSquare',
            link: `id/${tenant}/users/employees/employees-table`,
            permission: 'TENANT_USERS'
          },
          {
            header: 'Poczekalnia',
            icon: 'clock',
            link: `id/${tenant}/users/waiting-room`,
            permission: 'TENANT_USERS'
          },
        ]
      },
      {
        header: 'Raporty',
        icon: 'file',
        link: `id/${tenant}/reports`,
        permission: 'REPORTS'
      },
      {
        header: 'Finanse',
        icon: 'coinsStacked',
        permission: 'FINANCES',
        children: [
          {
            header: 'Lista transakcji',
            icon: 'coinsStacked',
            link: `id/${tenant}/finance/transaction-list`,
            permission: 'FINANCES'
          },
          {
            header: 'Podsumowanie',
            icon: 'coinsStacked',
            link: `id/${tenant}/finance/summary`,
            permission: 'FINANCES'
          }
        ]
      },
      {
        header: (
          <>
            Wiadomości{' '}
            {unreadThreadCount > 0 && (
              <span className="ml-2 mb-1.5 text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
                {unreadThreadCount}
              </span>
            )}
          </>
        ),
        icon: 'message',
        link: `id/${tenant}/mail-box/messages`,
        permission: 'MESSAGING'
      },
      {
        header: 'Ustawienia',
        icon: 'settings04',
        children: [
          {
            header: `Klasy`,
            icon: 'stand',
            link: `id/${tenant}/school/classes`,
            permission: 'TENANT_DATA'
          },
          {
            header: 'Dni wolne / blokady zamówień',
            icon: 'calendarDate',
            link: `id/${tenant}/school/free-days`,
            permission: 'TENANT_DATA'
          },
          {
            header: 'System sprzedaży',
            icon: 'sliders02',
            link: `id/${tenant}/kitchen/sales-system/servings`,
            permission: 'TENANT_DATA'
          },
          {
            header: 'Instytucje dofinansowujące',
            icon: 'coinsHand',
            link: `id/${tenant}/school/co-financing-institutions`,
            permission: 'TENANT_DATA'
          },
          {
            header: 'Regulaminy',
            icon: 'file06',
            link: `id/${tenant}/school/school-statutes`,
            permission: 'TENANT_DATA'
          },
          {
            header: 'Administratorzy',
            icon: 'imageUserCheck',
            link: `id/${tenant}/users/administrators/administrators-table`,
            permission: 'ADMINISTRATORS'
          },
          {
            header: 'System',
            icon: 'sliders02',
            link: `id/${tenant}/school/school-settings`,
            permission: 'TENANT_DATA'
          }
        ]
      }
//       ,      {
//         header: 'Instrukcje',
//         icon: 'question',
//         link: 'instructions',
//         permission: null
//       }
    ],
    [tenant, unreadThreadCount]
  );

  const filteredMenu = useMemo(
    () => filterMenuByPermissions(menuOptions, auth.loggedUserDto.availableModules),
    [menuOptions, auth.loggedUserDto.availableModules]
  );

  const menuList = (
    <>
      <MenuItem
        icon={
          <div className="Header__iconContainer">
            <IconComponent iconName="settings" className="w-full h-full" />
          </div>
        }
        onClick={function () {
          navigate('/tenantAdmin/settings');
        }}>
        Ustawienia
      </MenuItem>
      <MenuDivider />
      <MenuItem
        icon={
          <div className="Header__iconContainer">
            <IconComponent iconName="logOutDoor" className="w-full h-full" />
          </div>
        }
        onClick={async function () {
          logout();
        }}>
        Wyloguj się
      </MenuItem>
    </>
  );

  useEffect(() => {
    toggleShowNav(true);
    setNavOptions(filteredMenu);
  }, [tenant, filteredMenu]);

  const instructions: Instruction[] = [
    {
      url: 'https://www.youtube.com/watch?v=AcTfxJkt41c',
      title: 'Dodawanie ucznia',
      description: 'Instrukcja jak zgłaszać ucznia jako konsumenta posiłków'
    },
    {
      url: 'https://www.youtube.com/watch?v=WxMMHpWbnzw',
      title: 'Dodawanie breloka/numeru karty',
      description: 'Instrukcja jak dodać informacje o numerze identyfikatora do ucznia/konsumenta'
    }
  ];

  useEffect(() => {
    if (dashboardData?.unreadThreads != null) {
      setUnreadThreadCount(dashboardData?.unreadThreads);
    }
  }, [dashboardData?.unreadThreads]);

  return (
    <section className="flex flex-col h-full w-full overflow-x-hidden">
      {/* Header */}
      <div className="shrink-0 border-b border-brand-600">
        <WideHeader
          showLogo={false}
          dropdownMenuList={menuList}
          unreadMessageCount={unreadThreadCount}
        />
      </div>

      {/* Scrollable content area */}
      <div className={`flex-1 overflow-y-auto`}>
        <div className="min-h-full">
          <Routes>
            <Route
              path="/id/:id/dashboard/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['ORDERS']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<Dashboard />}
                />
              }
              index
            />
            <Route
              path="/id/:id/school/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['TENANT_DATA']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<School />}
                />
              }
            />
            <Route
              path="/id/:id/kitchen/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['MEAL_COOKING']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<Kitchen />}
                />
              }
            />
            <Route
              path="/id/:id/absences/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['ORDERS']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<Absences />}
                />
              }
            />
            <Route
              path="/settings/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['ADMINISTRATORS', 'TENANT_DATA']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<AdminSettings />}
                />
              }
            />
            <Route
              path="/id/:id/users/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['TENANT_USERS']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<Users />}
                />
              }
            />
            <Route
              path="/id/:id/reports/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['REPORTS']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<Reports />}
                />
              }
            />
            <Route
              path="/id/:id/finance/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['FINANCES']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<Finances />}
                />
              }
            />
            <Route
              path="/id/:id/mail-box/messages/message/:threadId/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['MESSAGING']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<SingleAdminMessage />}
                />
              }
            />
            <Route
              path="/id/:id/mail-box/announcement/:threadId*"
              element={
                <ProtectedRoute
                  requiredPermissions={['MESSAGING']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<SingleAnnouncement />}
                />
              }
            />
            <Route
              path="/id/:id/mail-box/*"
              element={
                <ProtectedRoute
                  requiredPermissions={['MESSAGING']}
                  menuOptions={menuOptions}
                  tenantId={tenant || ''}
                  element={<MailBox />}
                />
              }
            />
            <Route
              path="/instructions"
              element={
                <ParentDashboardContextProvider>
                  <Tutorials instructions={instructions} />
                </ParentDashboardContextProvider>
              }
            />
          </Routes>
        </div>
      </div>
    </section>
  );
};

export default TenantAdminDashboard;
